import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useFetch from 'use-http';
import {
  readBookingTrackingData, clearBookingTrackingData, pushBookingConfirmedEvents,
  readExponeaBookingPayload, clearExponeaBookingPayload, pushExponeaBookingResultEvent,
} from '../utils/bookingTracking';
import { getBookingConfirmation, getBookingStatusPath } from '../utils/bookingConfirmation';

// Mounted above the route switch so status verification survives the account-page redirect.
export default function BookingOutcomeTracker() {
  const { pathname } = useLocation();
  const [pending, setPending] = useState(null);
  const { get, response } = useFetch();
  const apiRef = useRef({ get, response });
  apiRef.current = { get, response };

  useEffect(() => {
    const kind = /^\/cart\/success(?:\/|$)/.test(pathname)
      || /^\/payment\/(?:accept|remoteAccept|ctxrmAccept|psdfAccept|socialAccept|couponAccept|remoteCouponAccept|psdfWithAdditionalPaymentAccept)(?:\/|$)/.test(pathname) ? 'registration'
      : /^\/kompleksines-paslaugos\/cart\/success(?:\/|$)/.test(pathname) ? 'complex' : null;
    if (!kind) return;
    const saved = readBookingTrackingData();
    if (saved?.kind === kind && getBookingStatusPath(saved)) setPending(saved);
  }, [pathname]);

  useEffect(() => {
    if (!pending) return;
    let disposed = false;
    let timeout;
    let attempts = 0;
    const isCurrent = () => {
      const current = readBookingTrackingData();
      return current?.kind === pending.kind && current?.bookingId === pending.bookingId;
    };
    const check = async () => {
      try {
        const status = await apiRef.current.get(getBookingStatusPath(pending));
        if (disposed || !isCurrent()) return;
        const confirmation = apiRef.current.response.ok && getBookingConfirmation(pending, status);
        if (confirmation?.confirmed) {
          pushBookingConfirmedEvents(pending, confirmation);
          clearBookingTrackingData();
          if (!pending.legacy) {
            try { pushExponeaBookingResultEvent(readExponeaBookingPayload(), 'Success'); }
            catch { /* Marketing SDK failures must not restart verified conversion polling. */ }
            finally { clearExponeaBookingPayload(); }
          }
          return;
        }
        if (confirmation?.terminal) {
          clearBookingTrackingData();
          return;
        }
      } catch { /* A provider callback or API can arrive after the browser return. */ }
      if (!disposed && isCurrent() && ++attempts < 20) timeout = setTimeout(check, 3000);
    };
    check();
    return () => { disposed = true; clearTimeout(timeout); };
  }, [pending]);
  return null;
}
